import type { AudioHandler } from '@mimir/cutter/player/audioHandler';
import type { AudioRequestFromWorker, AudioRequestFromMain } from '@mimir/browser/cutter/worker/protocol/audioProtocol';
import type { QueuedAudioFrame } from '@mimir/cutter/player/cutterCorePlayer';
import type { BufferCache } from '@mimir/common/util/bufferCache';

export function createAudioMainHandler(
  services: {
    bufferCache: BufferCache,
  },
  audioHandler: AudioHandler,
  postRequestFromMain: (request: AudioRequestFromMain, transfer?: Transferable[]) => void,
): ((request: AudioRequestFromWorker) => void) {
  const { bufferCache } = services;
  return (request: AudioRequestFromWorker) => {
    const requestType = request.type;
    switch (requestType) {
      case 'close': {
        audioHandler.close();
        break;
      }
      case 'stop_audio_playback': {
        audioHandler.stopAudioPlayback(request.clearQueue);
        break;
      }
      case 'start_audio_playback': {
        audioHandler.startAudioPlayback();
        break;
      }
      case 'queue_audio_frame': {
        const buffer =
          bufferCache.allocateUint8Array(
            request.buffer.length,
            (uint8Array: Uint8Array) => {
              uint8Array.set(request.buffer);
            },
          );

        const queuedAudioFrame : QueuedAudioFrame = {
          audioFrame: {
            ...request.info,
            sampleCount: request.sampleCount,
            sampleOffset: request.sampleOffset,
            buffer,
          },
          onAudioBufferDequeued: () => postRequestFromMain({
            type: 'audio_frame_dequeued',
            id: request.id,
            returnedBuffer: request.buffer,
          }, [request.buffer.buffer]),
          onAudioFrameStartPlaying: () => postRequestFromMain({
            type: 'audio_frame_start_playing',
            id: request.id,
          }),
          onCancelledPlayout: () => postRequestFromMain({
            type: 'audio_frame_cancelled_playout',
            id: request.id,
            returnedBuffer: request.buffer,
          }, [request.buffer.buffer]),
        };

        audioHandler.queueAudioFrame(queuedAudioFrame);
        break;
      }

      default:
        requestType satisfies never;
    }

  };
}
